import { useEffect, useRef, useState } from 'react';
import { Button, Icon, Popover, RadioGroup, Select, StatusIndicator } from '@amzn/awsui-components-react';
import {
  getIvtClient,
  GrowType,
  RetainReview,
  UpdateAssetCommand,
  type GrowMetrics,
} from '../../../client/IvtHubClient';
import { useAuth } from '../../../../context/AuthContext';
import { denialHint, hasIvtEditorAccess } from '../../../utils/authorization';
import { formatArr } from '../../DashboardPage/components/scale';
import { GROW_ORDER } from '../utils/assetGrouping';

/**
 * The two GROW cells of the asset Details card (DAT-9116 / DAT-9158): **GROW Type** and
 * **衡量指标**. Figma: page `4259:48345` — "GROW 四类样子" (display) and "编辑样子" (edit).
 *
 * - GROW Type is set by a person. The product is single-select for now, so the editor offers
 *   one type or 不设置 and sends a one-element list (or `[]` to clear). The backend stores a set,
 *   so a second type later is a frontend change only.
 * - 衡量指标 shows the metric that belongs to the chosen type. Gain / bOOst / Widen are computed
 *   offline twice a day for EVERY asset, so switching type shows the new figure at once — there
 *   is nothing to recompute. Retain is not computed: it is the Tech Committee's review, entered
 *   here (Passed / Failed; there is deliberately no way back to 未评审 yet — user, 2026-10-12).
 * - A metric that cannot be computed (e.g. the asset has no SFDC tag) arrives absent and shows
 *   「—」; a real 0 shows 0.
 *
 * Writes follow the header's other in-place edits: UpdateAsset with ONLY the changed member
 * (PATCH), an optimistic local value so the cell does not flash the old one while the page
 * reloads GetAsset, and the denial hint on a 403. The two editors share one saving/error
 * state, so both pencils are disabled while a save is in flight — otherwise opening the other
 * editor would close this one and its failure would surface under the wrong cell.
 *
 * The pencils render only for an editor or admin (DAT-9116 acceptance criteria). UX only: the
 * UpdateAsset Editor+ gate is the enforcement, which is why a 403 is still handled.
 */

/** Badge text, editor option label, and the badge colour class (Figma "GROW 四类样子"). */
export const GROW_META: Record<GrowType, { badge: string; option: string; className: string }> = {
  GAIN: { badge: 'Gain', option: 'Gain 渗透力', className: 'is-gain' },
  RETAIN: { badge: 'Retain', option: 'Retain 粘性力', className: 'is-retain' },
  BOOST: { badge: 'bOOst', option: 'bOOst 客单价力', className: 'is-boost' },
  WIDEN: { badge: 'Widen', option: 'Widen 延展力', className: 'is-widen' },
};

/** The editor's "no type" option. Not a GrowType: it is sent as `growTypes: []`. */
const NONE = 'NONE';

const ABSENT = '—';

/**
 * Coverage exactly as the backend computed it: the column is `NUMERIC(5,4)`, i.e. a percent
 * with two decimals, so that is all that is printed (trailing zeros dropped). Rounding any
 * further would show a tiny coverage as "0%" or a near-full one as "100%".
 */
export function formatCoverage(fraction: number | undefined): string {
  if (fraction == null || !Number.isFinite(fraction)) return ABSENT;
  return `${Number((fraction * 100).toFixed(2))}%`;
}

/** A computed metric's value alone ("12.34%", "3 笔", "$1.20M", or 「—」); also the GROW Menu
 *  tree's row prefix. Retain is a status, not a figure, so it has none. */
export function metricValue(type: GrowType, metrics: GrowMetrics | undefined): string {
  switch (type) {
    case GrowType.GAIN:
      return formatCoverage(metrics?.gainCoverage);
    case GrowType.BOOST:
      return metrics?.boostDealCount == null ? ABSENT : `${metrics.boostDealCount} 笔`;
    case GrowType.WIDEN:
      return metrics?.widenLaunchedArr == null ? ABSENT : formatArr(metrics.widenLaunchedArr);
    default:
      return ABSENT;
  }
}

/** The metric line for a type, without the Retain case (which is a status, not a figure). */
export function metricText(type: GrowType, metrics: GrowMetrics | undefined): string {
  switch (type) {
    case GrowType.GAIN:
      return `Account Coverage% = ${metricValue(type, metrics)}`;
    case GrowType.BOOST:
      return `Deal Size ≥ $200K ARR = ${metricValue(type, metrics)}`;
    case GrowType.WIDEN:
      return `Total Launched ARR = ${metricValue(type, metrics)}`;
    default:
      return ABSENT;
  }
}

/** "2026/10/12 08:30", pinned to Asia/Shanghai like the rest of the header. */
function formatComputedAt(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}/${get('month')}/${get('day')} ${get('hour')}:${get('minute')}`;
}

/**
 * What the four metrics mean, for business readers. Written from the computation itself
 * (IVTHubSpec tech/design/2026-10-10-asset-grow-metrics-design.md §1), not from the Figma
 * placeholder copy.
 */
function GrowMetricsHelp({ computedAt }: { computedAt: string | undefined }) {
  const at = formatComputedAt(computedAt);
  return (
    <div className="ivt-assetd-grow-help">
      <p>
        <strong>Gain 渗透力 · 客户覆盖率</strong>
        <br />
        资产所属行业里，有该资产商机的客户数 ÷ 该行业的全部客户数。
      </p>
      <p>
        <strong>Retain 粘性力 · 技术评审</strong>
        <br />
        由 Tech Committee 评审资产架构的粘性（主要看迁移难度），结果 Passed / Failed 在这里手工填写。
      </p>
      <p>
        <strong>bOOst 客单价力 · 大单数</strong>
        <br />
        该资产的商机中，ARR ≥ $200K 且没有丢单的商机数，已上线和进行中的都算。
      </p>
      <p>
        <strong>Widen 延展力 · 跟进后上线 ARR</strong>
        <br />
        对该资产商机所在的每个客户：以 IVT 成员在这些商机上的每条活动为起点，往后 30 天内该客户上线的全部商机
        ARR 之和（不论商机有没有打该资产的 tag）；再把各客户相加。
      </p>
      <p className="ivt-assetd-grow-help__note">
        Gain、bOOst、Widen 每天 08:30 和 20:30 自动更新，最近一次：{at ?? '尚未计算'}。
      </p>
    </div>
  );
}

export interface AssetGrowCellsProps {
  assetId: string | null;
  growTypes: GrowType[] | undefined;
  retainReview: RetainReview | undefined;
  growMetrics: GrowMetrics | undefined;
  /** After a successful write, so the page reloads GetAsset. */
  onSaved?: () => void;
}

export default function AssetGrowCells({
  assetId,
  growTypes,
  retainReview,
  growMetrics,
  onSaved,
}: AssetGrowCellsProps) {
  const { ldap_groups } = useAuth();
  const canEdit = hasIvtEditorAccess(ldap_groups);
  // Optimistic values: shown from a successful save until the reloaded detail carries them.
  const [localTypes, setLocalTypes] = useState<GrowType[] | undefined>(undefined);
  const [localReview, setLocalReview] = useState<RetainReview | undefined>(undefined);

  const [editingType, setEditingType] = useState(false);
  const [draftType, setDraftType] = useState<string>(NONE);
  const [editingReview, setEditingReview] = useState(false);
  const [draftReview, setDraftReview] = useState<RetainReview | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The header is not remounted on an asset change, so neither is this: drop everything
  // that belongs to the previous asset. The ref lets an in-flight save notice the switch.
  const assetIdRef = useRef(assetId);
  useEffect(() => {
    assetIdRef.current = assetId;
    setLocalTypes(undefined);
    setLocalReview(undefined);
    setEditingType(false);
    setEditingReview(false);
    setSaving(false);
    setError(null);
  }, [assetId]);

  // Output order is fixed server-side (GAIN, RETAIN, BOOST, WIDEN); with single-select there
  // is one, and the first is the one the metric cell follows if a set ever arrives.
  const types = localTypes ?? growTypes ?? [];
  const current = types[0];
  const review = localReview ?? retainReview ?? RetainReview.NOT_REVIEWED;

  const save = async (
    patch: { growTypes: GrowType[] } | { retainReview: RetainReview },
    apply: () => void,
  ) => {
    if (!assetId) return;
    const forAssetId = assetId;
    setSaving(true);
    setError(null);
    try {
      await getIvtClient().send(new UpdateAssetCommand({ assetId: forAssetId, ...patch }));
      if (assetIdRef.current !== forAssetId) return;
      apply();
      onSaved?.();
    } catch (e) {
      if (assetIdRef.current !== forAssetId) return;
      setError(denialHint(e) ?? ((e as Error).message || '保存失败'));
    } finally {
      if (assetIdRef.current === forAssetId) setSaving(false);
    }
  };

  const typeOptions = [
    ...GROW_ORDER.map((t) => ({ value: t, label: GROW_META[t].option })),
    { value: NONE, label: '不设置' },
  ];

  const startTypeEdit = () => {
    setDraftType(current ?? NONE);
    setEditingReview(false);
    setError(null);
    setEditingType(true);
  };
  const saveType = () => {
    const next = draftType === NONE ? [] : [draftType as GrowType];
    void save({ growTypes: next }, () => {
      setLocalTypes(next);
      setEditingType(false);
    });
  };

  const startReviewEdit = () => {
    setDraftReview(review === RetainReview.NOT_REVIEWED ? null : review);
    setEditingType(false);
    setError(null);
    setEditingReview(true);
  };
  const saveReview = () => {
    if (draftReview == null) return;
    const next = draftReview;
    void save({ retainReview: next }, () => {
      setLocalReview(next);
      setEditingReview(false);
    });
  };

  const editButtons = (what: string, onSave: () => void, onCancel: () => void, canSave = true) => (
    <>
      <Button
        variant="inline-icon"
        iconName="close"
        ariaLabel={`取消编辑${what}`}
        disabled={saving}
        onClick={onCancel}
      />
      <Button
        variant="inline-icon"
        iconName="check"
        ariaLabel={`保存${what}`}
        loading={saving}
        disabled={!canSave}
        onClick={onSave}
      />
    </>
  );

  const errorLine = error && (
    <div className="ivt-assetd-tag__error">
      <Icon name="status-warning" size="small" variant="error" /> {error}
    </div>
  );

  return (
    <>
      <div className="ivt-assetd-details__cell">
        <span className="ivt-assetd-details__label">GROW Type</span>
        {editingType ? (
          <span className="ivt-assetd-details__value ivt-assetd-grow-edit">
            <span className="ivt-assetd-grow-edit__select">
              <Select
                selectedOption={typeOptions.find((o) => o.value === draftType) ?? null}
                onChange={({ detail }) => setDraftType(detail.selectedOption.value ?? NONE)}
                options={typeOptions}
                placeholder="Choose an option"
                ariaLabel="GROW 类型"
                disabled={saving}
                expandToViewport
              />
            </span>
            {editButtons('GROW 类型', saveType, () => setEditingType(false))}
          </span>
        ) : (
          <span className="ivt-assetd-details__value ivt-assetd-grow-row">
            {types.length > 0 ? (
              types.map((t) => (
                <span key={t} className={`ivt-assetd-grow-badge ${GROW_META[t].className}`}>
                  {GROW_META[t].badge}
                </span>
              ))
            ) : (
              <span className="ivt-assetd-details__value--muted">{ABSENT}</span>
            )}
            {canEdit && (
              <Button
                variant="inline-icon"
                iconName="edit"
                ariaLabel="编辑 GROW 类型"
                disabled={saving}
                onClick={startTypeEdit}
              />
            )}
          </span>
        )}
        {editingType && errorLine}
      </div>

      <div className="ivt-assetd-details__cell">
        <span className="ivt-assetd-details__label ivt-assetd-details__label-row">
          衡量指标
          <Popover dismissButton={false} position="top" size="large" triggerType="custom" content={<GrowMetricsHelp computedAt={growMetrics?.computedAt} />}>
            <button type="button" className="ivt-assetd-info-btn" aria-label="GROW 衡量指标说明">
              <Icon name="status-info" size="small" variant="link" />
            </button>
          </Popover>
        </span>
        {current === GrowType.RETAIN ? (
          editingReview ? (
            <span className="ivt-assetd-details__value ivt-assetd-grow-edit">
              <RadioGroup
                direction="horizontal"
                value={draftReview}
                onChange={({ detail }) => setDraftReview(detail.value as RetainReview)}
                items={[
                  { value: RetainReview.PASSED, label: 'Passed' },
                  { value: RetainReview.FAILED, label: 'Failed' },
                ]}
                ariaLabel="Retain 技术评审结果"
              />
              {editButtons('技术评审结果', saveReview, () => setEditingReview(false), draftReview != null)}
            </span>
          ) : (
            <span className="ivt-assetd-details__value ivt-assetd-grow-row">
              {review === RetainReview.PASSED ? (
                <StatusIndicator type="success">Passed</StatusIndicator>
              ) : review === RetainReview.FAILED ? (
                <StatusIndicator type="error">Failed</StatusIndicator>
              ) : (
                <span className="ivt-assetd-details__value--muted">未评审</span>
              )}
              {canEdit && (
                <Button
                  variant="inline-icon"
                  iconName="edit"
                  ariaLabel="编辑技术评审结果"
                  disabled={saving}
                  onClick={startReviewEdit}
                />
              )}
            </span>
          )
        ) : (
          <span
            className={
              current ? 'ivt-assetd-details__value' : 'ivt-assetd-details__value ivt-assetd-details__value--muted'
            }
          >
            {current ? metricText(current, growMetrics) : ABSENT}
          </span>
        )}
        {editingReview && errorLine}
      </div>
    </>
  );
}
